/**
 * components/object/AlignmentInspector.jsx — Iter 50.
 *
 * Treats an Alignment as a first-class unified object. Reuses the Iter 47
 * ObjectInspector (Overview / Photos / Timeline tabs + notes + issues +
 * status). All operational memory is recorded against the alignment's id
 * via `/api/objects/alignment/{id}/…` — same plumbing as structures.
 *
 * Surfaces alignment-specific facts in the header slot:
 *   - kind (centerline / right_of_way / utility / contour …)
 *   - begin → end station with calm chip
 *   - total length in units (ft/m)
 *   - sheet pill for context
 *
 * NO geometry editor. NO design tools. View + record only.
 */
import React, { useState } from "react";
import { X, Route, Ruler, Trash2 } from "lucide-react";
import ObjectInspector from "@/components/object/ObjectInspector";
import ObjectRelations from "@/components/object/ObjectRelations";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABEL = {
  centerline: "Centerline",
  right_of_way: "Right of Way",
  contour: "Contour",
  utility: "Utility Line",
  property_line: "Property Line",
};

export default function AlignmentInspector({ alignment, onClose }) {
  const [deleting, setDeleting] = useState(false);
  if (!alignment) return null;
  const isArchived = !!alignment.archived;
  const units = alignment.units || "ft";
  const beginLabel = formatStation(alignment.begin_station, units);
  const endLabel = formatStation(
    (alignment.begin_station || 0) + (alignment.length_units || 0), units);
  const lenLabel = `${Math.round(alignment.length_units || 0)} ${units}`;

  const onDelete = async () => {
    if (!window.confirm(`Delete alignment "${alignment.name || alignment.id}"?`)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/alignments/${alignment.id}`);
      toast.success(data.mode === "hard_delete" ? "Alignment deleted" : "Alignment archived");
      window.dispatchEvent(new CustomEvent("forgedops:alignments-updated"));
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "has_dependents") {
        const counts = Object.entries(detail.dependent_counts || {})
          .map(([k, v]) => `${v} ${k}`).join(", ");
        if (window.confirm(
          `${detail.message}\n\nDependent records: ${counts}.\n\nArchive this alignment instead? (nothing downstream is removed)`
        )) {
          try {
            const { data } = await api.delete(`/alignments/${alignment.id}`, { params: { confirm: true } });
            toast.success("Alignment archived — dependent records untouched");
            window.dispatchEvent(new CustomEvent("forgedops:alignments-updated"));
            onClose?.();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail?.message || e2?.response?.data?.detail || "Failed to archive alignment");
          }
        }
      } else {
        toast.error(detail?.message || detail || "Failed to delete alignment");
      }
    } finally {
      setDeleting(false);
    }
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider"
          data-testid="alignment-header-meta">
      {isArchived && (
        <span className="px-1.5 py-0.5 rounded border border-[var(--fop-warn)] text-[var(--fop-warn)]"
              data-testid="alignment-archived-badge">
          Archived
        </span>
      )}
      <span className="px-1.5 py-0.5 rounded border border-[var(--fop-border)] bg-[var(--fop-surface-2)] text-[var(--fop-text-2)]">
        <Route size={9} className="inline -mt-0.5 mr-1" />
        {KIND_LABEL[alignment.kind] || alignment.kind || "alignment"}
      </span>
      <span className="text-[var(--fop-text-2)]">
        <b className="text-[var(--fop-primary)] font-semibold">{beginLabel}</b>
        <span className="opacity-60 mx-1">→</span>
        <b className="text-[var(--fop-primary)] font-semibold">{endLabel}</b>
      </span>
      <span className="text-[var(--fop-text-3)]">
        <Ruler size={9} className="inline -mt-0.5 mr-1" />
        {lenLabel}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="alignment-inspector"
          data-alignment-id={alignment.id}>
      <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-start gap-3">
        <Route size={14} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            Alignment
          </div>
          <div className="font-mono text-sm font-bold text-[var(--fop-text)] truncate"
                data-testid="alignment-inspector-name">
            {alignment.name || alignment.id}
          </div>
        </div>
        <button onClick={onDelete} disabled={deleting}
                 className="text-[var(--fop-text-3)] hover:text-[var(--fop-danger)] disabled:opacity-40 -mr-1"
                 aria-label="Delete alignment" data-testid="alignment-delete-button">
          <Trash2 size={14} />
        </button>
        <button onClick={onClose}
                 className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] -mr-1"
                 aria-label="Close inspector" data-testid="alignment-inspector-close">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <ObjectInspector
          objectType="alignment" objectId={alignment.id}
          headerSlot={header}
        />
        {/* Relations rail — same object-agnostic component */}
        <div className="border-t border-[var(--fop-border)] p-3">
          <ObjectRelations objectType="alignment" objectId={alignment.id} />
        </div>
      </div>
    </div>
  );
}

function formatStation(stationUnits, units = "ft") {
  if (stationUnits == null || !Number.isFinite(stationUnits)) return "—";
  if (stationUnits < 0) return `STA -${formatStation(-stationUnits, units).slice(4)}`;
  const block = Math.floor(stationUnits / 100);
  const rem = stationUnits - block * 100;
  if (Math.abs(rem - Math.round(rem)) < 0.05) {
    return `STA ${block}+${String(Math.round(rem)).padStart(2, "0")}`;
  }
  return `STA ${block}+${rem.toFixed(2).padStart(5, "0")}`;
}
